import LogoutIcon from "@mui/icons-material/Logout";
import PersonOutlineIcon from "@mui/icons-material/PersonOutlineOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import ThemeLanguageControls from "@/components/common/ThemeLanguageControls";
import { useAccess } from "@/hooks/useAccess";
import { useSessionStore } from "@/store/session.store";
import { usePlatformSessionStore } from "@/store/platformSession.store";
import { useWorkspaceStore } from "@/store/workspace.store";

function initials(name) {
  if (!name) return "?";
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function HeaderAccountMenu({ variant = "customer" }) {
  const { t } = useTranslation();
  const customerUser = useSessionStore((s) => s.user);
  const customerSignOut = useSessionStore((s) => s.signOut);
  const platformOperator = usePlatformSessionStore((s) => s.operator);
  const platformSignOut = usePlatformSessionStore((s) => s.signOut);
  const user =
    variant === "platform"
      ? platformOperator
        ? { name: platformOperator.name, email: platformOperator.email }
        : null
      : customerUser;
  const { role } = useAccess();
  const displayRole = variant === "platform" ? "Platform operator" : role;
  const isPreview = useWorkspaceStore((s) => s.isCustomerPreview);
  const endCustomerPreview = useWorkspaceStore((s) => s.endCustomerPreview);
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(null);

  const handleLogout = () => {
    setAnchor(null);
    if (variant === "platform") {
      platformSignOut();
      navigate("/platform/login");
      return;
    }
    if (isPreview()) {
      navigate(endCustomerPreview());
    } else {
      customerSignOut();
      navigate("/login");
    }
  };

  return (
    <>
      <IconButton
        data-testid="account-menu-btn"
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ p: 0.5 }}
        aria-label={t("common.profile")}
      >
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          variant="dot"
          sx={{
            "& .MuiBadge-badge": {
              bgcolor: "#22c55e",
              border: "2px solid",
              borderColor: "background.paper",
              width: 10,
              height: 10,
            },
          }}
        >
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: "primary.main",
              fontWeight: 700,
              fontSize: "0.9rem",
            }}
          >
            {initials(user?.name)}
          </Avatar>
        </Badge>
      </IconButton>
      <Menu
        data-testid="account-menu"
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: { width: 300, borderRadius: 2.5, mt: 1.5 },
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <Avatar sx={{ width: 44, height: 44, bgcolor: "primary.main" }}>
              {initials(user?.name)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800 }} noWrap>
                {user?.name}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.8rem" }}
                noWrap
              >
                {user?.email}
              </Typography>
              <Chip size="small" label={displayRole} sx={{ mt: 0.75, height: 22 }} />
            </Box>
          </Box>
        </Box>
        <Divider />
        {variant === "customer" && (
          <MenuItem
            component={RouterLink}
            to="/app/settings"
            onClick={() => setAnchor(null)}
            sx={{ py: 1 }}
          >
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            {t("common.settings")}
          </MenuItem>
        )}
        <MenuItem onClick={() => setAnchor(null)} sx={{ py: 1 }}>
          <ListItemIcon>
            <PersonOutlineIcon fontSize="small" />
          </ListItemIcon>
          {t("common.profile")}
        </MenuItem>
        <Divider />
        <Box
          sx={{
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t("common.appearance")}
          </Typography>
          <ThemeLanguageControls compact />
        </Box>
        <Divider />
        <MenuItem
          data-testid="logout-btn"
          onClick={handleLogout}
          sx={{ color: "error.main", py: 1 }}
        >
          <ListItemIcon sx={{ color: "error.main" }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          {isPreview() ? t("workspace.previewBanner").slice(0, 12) + "…" : t("common.signOut")}
        </MenuItem>
      </Menu>
    </>
  );
}

export default HeaderAccountMenu;
